// ===============================================================================
// ExpenseHelp.js - Expense Module Help & Documentation
// ===============================================================================
// Location: /var/www/wow/frontend/src/ExpenseHelp.js
// Comprehensive guide explaining how the expense module works
// Examples using: Elias, Maria, Dimitroula
// ===============================================================================

import React, { useState } from "react";
import "./ExpenseHelp.css";

function ExpenseHelp({ onClose }) {
  const [activeSection, setActiveSection] = useState("overview");

  // ===============================================================================
  // Example Data
  // ===============================================================================
  const members = [
    { name: "Elias", avatar: "E", color: "#2d5a3d" },
    { name: "Maria", avatar: "M", color: "#5a8f6e" },
    { name: "Dimitroula", avatar: "D", color: "#7ab592" },
  ];

  const currency = "EUR";

  // ===============================================================================
  // Navigation Sections
  // ===============================================================================
  const sections = [
    { id: "overview", label: "📋 Overview", icon: "📋" },
    { id: "add-expense", label: "➕ Adding Expenses", icon: "➕" },
    { id: "single-payer", label: "💳 Single Payer", icon: "💳" },
    { id: "multiple-payers", label: "👥 Multiple Payers", icon: "👥" },
    { id: "split-percentage", label: "📊 Percentage Split", icon: "📊" },
    { id: "split-equal", label: "⚖️ Equal Split", icon: "⚖️" },
    { id: "split-shares", label: "👨‍👩‍👧 Shares/Guests", icon: "👨‍👩‍👧" },
    { id: "expense-detail", label: "🔍 Expense Details", icon: "🔍" },
    { id: "defaults", label: "⚙️ Default Settings", icon: "⚙️" },
    { id: "examples", label: "📝 Full Examples", icon: "📝" },
  ];

  // ===============================================================================
  // Render Section Content
  // ===============================================================================
  const renderContent = () => {
    switch (activeSection) {
      case "overview":
        return <OverviewSection members={members} />;
      case "add-expense":
        return <AddExpenseSection members={members} currency={currency} />;
      case "single-payer":
        return <SinglePayerSection members={members} currency={currency} />;
      case "multiple-payers":
        return <MultiplePayersSection members={members} currency={currency} />;
      case "split-percentage":
        return <SplitPercentageSection members={members} currency={currency} />;
      case "split-equal":
        return <SplitEqualSection members={members} currency={currency} />;
      case "split-shares":
        return <SplitSharesSection members={members} currency={currency} />;
      case "expense-detail":
        return <ExpenseDetailSection members={members} currency={currency} />;
      case "defaults":
        return <DefaultsSection members={members} />;
      case "examples":
        return <FullExamplesSection members={members} currency={currency} />;
      default:
        return <OverviewSection members={members} />;
    }
  };

  return (
    <div className="expense-help">
      <div className="help-header">
        <h1>📚 Expense Module Guide</h1>
        <p>Learn how to track and split expenses with your group</p>
        {onClose && (
          <button className="help-close-btn" onClick={onClose}>✕</button>
        )}
      </div>

      <div className="help-container">
        {/* Navigation Sidebar */}
        <nav className="help-nav">
          {sections.map((section) => (
            <button
              key={section.id}
              className={`help-nav-item ${activeSection === section.id ? "active" : ""}`}
              onClick={() => setActiveSection(section.id)}
            >
              <span className="nav-icon">{section.icon}</span>
              <span className="nav-label">{section.label.replace(/^[^\s]+\s/, "")}</span>
            </button>
          ))}
        </nav>

        {/* Content Area */}
        <main className="help-content">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Overview
// ===============================================================================
function OverviewSection({ members }) {
  return (
    <div className="help-section">
      <h2>📋 Overview</h2>
      
      <div className="intro-box">
        <p>
          The <strong>Expense Module</strong> helps you track shared expenses within your group 
          and automatically calculates who owes whom. Whether you're splitting a dinner bill, 
          sharing vacation costs, or managing household expenses, this module handles it all.
        </p>
      </div>

      <h3>👥 Meet Our Example Group</h3>
      <p>Throughout this guide, we'll use three friends who share expenses together:</p>
      
      <div className="members-showcase">
        {members.map((m) => (
          <div key={m.name} className="member-card" style={{ borderColor: m.color }}>
            <div className="member-avatar-lg" style={{ background: m.color }}>{m.avatar}</div>
            <span className="member-name-lg">{m.name}</span>
          </div>
        ))}
      </div>

      <h3>🔑 Key Features</h3>
      <div className="features-grid">
        <div className="feature-card">
          <div className="feature-icon">💳</div>
          <h4>Single or Multiple Payers</h4>
          <p>One person can pay, or multiple people can contribute to the same expense</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">📊</div>
          <h4>Flexible Splitting</h4>
          <p>Split by percentage, equally, or by shares (for guests/family members)</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">⚙️</div>
          <h4>Default Settings</h4>
          <p>Set default percentages and shares for quick expense entry</p>
        </div>
        <div className="feature-card">
          <div className="feature-icon">🔍</div>
          <h4>Detailed Breakdown</h4>
          <p>Click any expense to see exactly who paid, who owes, and net effects</p>
        </div>
      </div>

      <h3>📱 Workflow Summary</h3>
      <div className="workflow-steps">
        <div className="workflow-step">
          <div className="step-number">1</div>
          <div className="step-content">
            <strong>Enter Amount & Description</strong>
            <p>How much was spent and what for?</p>
          </div>
        </div>
        <div className="workflow-arrow">→</div>
        <div className="workflow-step">
          <div className="step-number">2</div>
          <div className="step-content">
            <strong>Who Paid?</strong>
            <p>Single payer or multiple contributors</p>
          </div>
        </div>
        <div className="workflow-arrow">→</div>
        <div className="workflow-step">
          <div className="step-number">3</div>
          <div className="step-content">
            <strong>How to Split?</strong>
            <p>Percentage, equal, or by shares</p>
          </div>
        </div>
        <div className="workflow-arrow">→</div>
        <div className="workflow-step">
          <div className="step-number">4</div>
          <div className="step-content">
            <strong>Save & Track</strong>
            <p>Balances update automatically</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Add Expense
// ===============================================================================
function AddExpenseSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>➕ Adding an Expense</h2>
      
      <p>To add a new expense, click the <strong>"➕ Add Expense"</strong> button in the group header.</p>

      <h3>📝 The Add Expense Form</h3>
      
      {/* Simulated Form */}
      <div className="screen-simulation">
        <div className="sim-header">Add Expense</div>
        <div className="sim-form">
          <div className="sim-form-row">
            <div className="sim-form-group">
              <label>Amount ({currency}) *</label>
              <input type="text" value="120.00" readOnly className="sim-input" />
            </div>
            <div className="sim-form-group">
              <label>Category</label>
              <select className="sim-input" defaultValue="food">
                <option value="food">🍕 Food & Drinks</option>
              </select>
            </div>
          </div>
          <div className="sim-form-group full">
            <label>Description *</label>
            <input type="text" value="Dinner at Greek Taverna" readOnly className="sim-input" />
          </div>
          <div className="sim-checkbox">
            <input type="checkbox" id="mp" />
            <label htmlFor="mp">☐ Multiple Payers</label>
          </div>
        </div>
        <div className="sim-annotation">
          <div className="annotation-line" style={{ top: "60px", left: "-10px" }}></div>
          <div className="annotation-text" style={{ top: "50px", left: "-180px" }}>
            Enter the total expense amount
          </div>
        </div>
      </div>

      <h3>📋 Form Fields Explained</h3>
      <table className="help-table">
        <thead>
          <tr>
            <th>Field</th>
            <th>Required</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td><strong>Amount</strong></td>
            <td>✅ Yes</td>
            <td>The total expense amount in the group's currency</td>
          </tr>
          <tr>
            <td><strong>Category</strong></td>
            <td>❌ No</td>
            <td>Optional category for organization (Food, Transport, etc.)</td>
          </tr>
          <tr>
            <td><strong>Description</strong></td>
            <td>✅ Yes</td>
            <td>What the expense was for (e.g., "Dinner at Greek Taverna")</td>
          </tr>
          <tr>
            <td><strong>Multiple Payers</strong></td>
            <td>❌ No</td>
            <td>Check this if more than one person paid</td>
          </tr>
        </tbody>
      </table>

      <div className="tip-box">
        <div className="tip-icon">💡</div>
        <div className="tip-content">
          <strong>Tip:</strong> The person adding the expense is automatically set as the payer 
          (unless you enable "Multiple Payers")
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Single Payer
// ===============================================================================
function SinglePayerSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>💳 Single Payer Mode</h2>
      
      <p>
        This is the <strong>default mode</strong>. When one person pays for an expense, 
        the system automatically records them as the payer.
      </p>

      <h3>📖 Example Scenario</h3>
      <div className="scenario-box">
        <div className="scenario-icon">🍕</div>
        <div className="scenario-content">
          <strong>Elias</strong> pays €90 for pizza delivery for the group.
        </div>
      </div>

      <h3>📱 What You See</h3>
      <div className="screen-simulation">
        <div className="sim-header">Add Expense</div>
        <div className="sim-form">
          <div className="sim-form-row">
            <div className="sim-form-group">
              <label>Amount (EUR) *</label>
              <input type="text" value="90.00" readOnly className="sim-input" />
            </div>
            <div className="sim-form-group">
              <label>Category</label>
              <select className="sim-input" defaultValue="food">
                <option value="food">🍕 Food & Drinks</option>
              </select>
            </div>
          </div>
          <div className="sim-form-group full">
            <label>Description *</label>
            <input type="text" value="Pizza delivery" readOnly className="sim-input" />
          </div>
          <div className="sim-checkbox">
            <input type="checkbox" readOnly />
            <label>☐ Multiple Payers</label>
            <span className="sim-note">(unchecked = Elias pays alone)</span>
          </div>
        </div>
      </div>

      <h3>📊 How It's Recorded</h3>
      <div className="result-breakdown">
        <div className="breakdown-section">
          <h4>💳 Paid By</h4>
          <div className="breakdown-row highlight-green">
            <span className="breakdown-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
            </span>
            <span className="breakdown-amount">+€90.00</span>
          </div>
        </div>
      </div>

      <div className="info-box">
        <div className="info-icon">ℹ️</div>
        <div className="info-content">
          <strong>Note:</strong> In single payer mode, the logged-in user who creates 
          the expense is automatically set as the payer.
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Multiple Payers
// ===============================================================================
function MultiplePayersSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>👥 Multiple Payers Mode</h2>
      
      <p>
        When <strong>more than one person</strong> contributes to paying for an expense, 
        enable the "Multiple Payers" checkbox. This reveals fields for each member to 
        enter their contribution.
      </p>

      <h3>📖 Example Scenario</h3>
      <div className="scenario-box">
        <div className="scenario-icon">🍽️</div>
        <div className="scenario-content">
          The group has dinner costing <strong>€150</strong>. When the bill comes:
          <ul>
            <li><strong>Elias</strong> pays €80 (cash)</li>
            <li><strong>Maria</strong> pays €50 (card)</li>
            <li><strong>Dimitroula</strong> pays €20 (cash)</li>
          </ul>
        </div>
      </div>

      <h3>📱 Step 1: Enable Multiple Payers</h3>
      <div className="screen-simulation">
        <div className="sim-header">Add Expense</div>
        <div className="sim-form">
          <div className="sim-form-row">
            <div className="sim-form-group">
              <label>Amount (EUR) *</label>
              <input type="text" value="150.00" readOnly className="sim-input" />
            </div>
            <div className="sim-form-group">
              <label>Category</label>
              <select className="sim-input" defaultValue="food">
                <option value="food">🍕 Food & Drinks</option>
              </select>
            </div>
          </div>
          <div className="sim-form-group full">
            <label>Description *</label>
            <input type="text" value="Group dinner at restaurant" readOnly className="sim-input" />
          </div>
          <div className="sim-checkbox checked">
            <input type="checkbox" checked readOnly />
            <label>☑️ Multiple Payers</label>
            <span className="sim-note highlight">(checked!)</span>
          </div>
        </div>
      </div>

      <h3>📱 Step 2: Enter Each Person's Payment</h3>
      <div className="screen-simulation">
        <div className="sim-section-header">💳 Who Paid?</div>
        <div className="sim-split-list">
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
            </span>
            <div className="sim-input-group">
              <input type="text" value="80.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
              Maria
            </span>
            <div className="sim-input-group">
              <input type="text" value="50.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
              Dimitroula
            </span>
            <div className="sim-input-group">
              <input type="text" value="20.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-total valid">
            Total: €150.00 ✓
          </div>
        </div>
      </div>

      <h3>⚠️ Validation Rule</h3>
      <div className="warning-box">
        <div className="warning-icon">⚠️</div>
        <div className="warning-content">
          The <strong>sum of all payer amounts</strong> must equal the <strong>expense total</strong>.
          <br/><br/>
          <div className="validation-example">
            <span className="calc">€80 + €50 + €20 = €150 ✓</span>
          </div>
        </div>
      </div>

      <h3>📊 How It's Recorded</h3>
      <div className="result-breakdown">
        <div className="breakdown-section">
          <h4>💳 Paid By</h4>
          <div className="breakdown-row highlight-green">
            <span className="breakdown-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
            </span>
            <span className="breakdown-amount">+€80.00</span>
          </div>
          <div className="breakdown-row highlight-green">
            <span className="breakdown-name">
              <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
              Maria
            </span>
            <span className="breakdown-amount">+€50.00</span>
          </div>
          <div className="breakdown-row highlight-green">
            <span className="breakdown-name">
              <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
              Dimitroula
            </span>
            <span className="breakdown-amount">+€20.00</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Split by Percentage
// ===============================================================================
function SplitPercentageSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>📊 Percentage Split</h2>
      
      <p>
        The <strong>default split method</strong>. Uses each member's default percentage 
        to calculate their share. These percentages are set in the Members tab and can be 
        adjusted for individual expenses.
      </p>

      <h3>📖 Example Scenario</h3>
      <div className="scenario-box">
        <div className="scenario-icon">🏠</div>
        <div className="scenario-content">
          Monthly utility bill of <strong>€100</strong>. The group has agreed on default percentages:
          <ul>
            <li><strong>Elias:</strong> 50% (larger room)</li>
            <li><strong>Maria:</strong> 30%</li>
            <li><strong>Dimitroula:</strong> 20%</li>
          </ul>
        </div>
      </div>

      <h3>📱 Split Options Panel</h3>
      <div className="screen-simulation">
        <div className="sim-section-header">📊 Split Options</div>
        <div className="sim-tabs">
          <button className="sim-tab active">% Default</button>
          <button className="sim-tab">= Equal</button>
          <button className="sim-tab">👥 Shares</button>
        </div>
        <div className="sim-split-list">
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
              <span className="pct-hint">(50%)</span>
            </span>
            <div className="sim-input-group">
              <input type="text" value="50.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
              Maria
              <span className="pct-hint">(30%)</span>
            </span>
            <div className="sim-input-group">
              <input type="text" value="30.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
              Dimitroula
              <span className="pct-hint">(20%)</span>
            </span>
            <div className="sim-input-group">
              <input type="text" value="20.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-total valid">
            Split Total: €100.00 ✓
          </div>
        </div>
      </div>

      <h3>🧮 How It's Calculated</h3>
      <div className="calculation-box">
        <div className="calc-formula">
          <strong>Formula:</strong> Amount × (Percentage ÷ 100)
        </div>
        <div className="calc-examples">
          <div className="calc-row">
            <span>Elias:</span>
            <span>€100 × 50% = <strong>€50.00</strong></span>
          </div>
          <div className="calc-row">
            <span>Maria:</span>
            <span>€100 × 30% = <strong>€30.00</strong></span>
          </div>
          <div className="calc-row">
            <span>Dimitroula:</span>
            <span>€100 × 20% = <strong>€20.00</strong></span>
          </div>
          <div className="calc-total">
            <span>Total:</span>
            <span>€50 + €30 + €20 = <strong>€100.00 ✓</strong></span>
          </div>
        </div>
      </div>

      <div className="tip-box">
        <div className="tip-icon">💡</div>
        <div className="tip-content">
          <strong>Tip:</strong> You can manually edit the amounts even in percentage mode. 
          This is useful for one-time adjustments without changing the default percentages.
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Equal Split
// ===============================================================================
function SplitEqualSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>⚖️ Equal Split</h2>
      
      <p>
        The simplest method: <strong>divide the total equally</strong> among all members.
        Click the "= Equal" tab or the "⚖️ Split Equally" button.
      </p>

      <h3>📖 Example Scenario</h3>
      <div className="scenario-box">
        <div className="scenario-icon">🎬</div>
        <div className="scenario-content">
          Movie tickets costing <strong>€45</strong> for all three friends.
          Everyone pays the same amount.
        </div>
      </div>

      <h3>📱 Split Options Panel</h3>
      <div className="screen-simulation">
        <div className="sim-section-header">📊 Split Options</div>
        <div className="sim-tabs">
          <button className="sim-tab">% Default</button>
          <button className="sim-tab active">= Equal</button>
          <button className="sim-tab">👥 Shares</button>
        </div>
        <div className="sim-split-list">
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
            </span>
            <div className="sim-input-group">
              <input type="text" value="15.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
              Maria
            </span>
            <div className="sim-input-group">
              <input type="text" value="15.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
              Dimitroula
            </span>
            <div className="sim-input-group">
              <input type="text" value="15.00" readOnly className="sim-input small" />
              <span className="sim-currency">EUR</span>
            </div>
          </div>
          <div className="sim-total valid">
            Split Total: €45.00 ✓
          </div>
        </div>
      </div>

      <h3>🧮 How It's Calculated</h3>
      <div className="calculation-box">
        <div className="calc-formula">
          <strong>Formula:</strong> Amount ÷ Number of Members
        </div>
        <div className="calc-examples">
          <div className="calc-row">
            <span>Each person:</span>
            <span>€45 ÷ 3 = <strong>€15.00</strong></span>
          </div>
          <div className="calc-total">
            <span>Total:</span>
            <span>€15 + €15 + €15 = <strong>€45.00 ✓</strong></span>
          </div>
        </div>
      </div>

      <h3>🔢 Handling Remainders</h3>
      <div className="info-box">
        <div className="info-icon">ℹ️</div>
        <div className="info-content">
          When the amount doesn't divide evenly (e.g., €100 ÷ 3), the system adds 
          the remainder to the first person's share to ensure the total is exact.
          <br/><br/>
          <strong>Example:</strong> €100 ÷ 3 = €33.33 each
          <br/>
          → Elias: €33.34, Maria: €33.33, Dimitroula: €33.33
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Shares/Guests Split
// ===============================================================================
function SplitSharesSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>👨‍👩‍👧 Shares/Guests Split</h2>
      
      <p>
        Perfect for when members bring <strong>guests, family members, or have different 
        consumption levels</strong>. Each member specifies their number of "shares" 
        (member + guests), and the expense is divided accordingly.
      </p>

      <h3>📖 Example Scenario</h3>
      <div className="scenario-box">
        <div className="scenario-icon">🍖</div>
        <div className="scenario-content">
          BBQ party costing <strong>€110</strong>. People brought family:
          <ul>
            <li><strong>Elias:</strong> Came alone (1 share)</li>
            <li><strong>Maria:</strong> Brought her husband + 2 kids (4 shares)</li>
            <li><strong>Dimitroula:</strong> Brought her partner (2 shares)</li>
          </ul>
          <strong>Total: 7 shares</strong>
        </div>
      </div>

      <h3>📱 Split Options Panel</h3>
      <div className="screen-simulation">
        <div className="sim-section-header">📊 Split Options</div>
        <div className="sim-tabs">
          <button className="sim-tab">% Default</button>
          <button className="sim-tab">= Equal</button>
          <button className="sim-tab active">👥 Shares</button>
        </div>
        <p className="sim-hint">Enter total shares per member (member + guests)</p>
        <div className="sim-split-list shares-mode">
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
            </span>
            <div className="sim-shares-input">
              <input type="text" value="1" readOnly className="sim-input tiny" />
              <span className="shares-label">shares</span>
            </div>
            <span className="shares-result">= €15.71</span>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
              Maria
            </span>
            <div className="sim-shares-input">
              <input type="text" value="4" readOnly className="sim-input tiny" />
              <span className="shares-label">shares</span>
            </div>
            <span className="shares-result">= €62.86</span>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
              Dimitroula
            </span>
            <div className="sim-shares-input">
              <input type="text" value="2" readOnly className="sim-input tiny" />
              <span className="shares-label">shares</span>
            </div>
            <span className="shares-result">= €31.43</span>
          </div>
          <div className="sim-total-shares">
            Total: 7 shares
          </div>
          <div className="sim-total valid">
            Split Total: €110.00 ✓
          </div>
        </div>
      </div>

      <h3>🧮 How It's Calculated</h3>
      <div className="calculation-box">
        <div className="calc-formula">
          <strong>Step 1:</strong> Per-share amount = Total ÷ Total shares
          <br/>
          <strong>Step 2:</strong> Member amount = Per-share × Member's shares
        </div>
        <div className="calc-examples">
          <div className="calc-row highlight">
            <span>Per share:</span>
            <span>€110 ÷ 7 = <strong>€15.71</strong></span>
          </div>
          <div className="calc-divider"></div>
          <div className="calc-row">
            <span>Elias (1 share):</span>
            <span>€15.71 × 1 = <strong>€15.71</strong></span>
          </div>
          <div className="calc-row">
            <span>Maria (4 shares):</span>
            <span>€15.71 × 4 = <strong>€62.86</strong></span>
          </div>
          <div className="calc-row">
            <span>Dimitroula (2 shares):</span>
            <span>€15.71 × 2 = <strong>€31.43</strong></span>
          </div>
          <div className="calc-total">
            <span>Total:</span>
            <span>€15.71 + €62.86 + €31.43 = <strong>€110.00 ✓</strong></span>
          </div>
        </div>
      </div>

      <h3>💡 When to Use Shares</h3>
      <div className="use-cases">
        <div className="use-case">
          <div className="use-icon">👨‍👩‍👧‍👦</div>
          <div className="use-text">Members bring family to events</div>
        </div>
        <div className="use-case">
          <div className="use-icon">🍽️</div>
          <div className="use-text">Someone ate/drank more</div>
        </div>
        <div className="use-case">
          <div className="use-icon">🏠</div>
          <div className="use-text">Different room sizes in shared housing</div>
        </div>
        <div className="use-case">
          <div className="use-icon">🚗</div>
          <div className="use-text">Car usage based on passengers</div>
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Expense Detail
// ===============================================================================
function ExpenseDetailSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>🔍 Expense Details View</h2>
      
      <p>
        Click on any expense in the list to see a <strong>detailed breakdown</strong> of 
        who paid, how it was split, and the net effect on each member.
      </p>

      <h3>📱 Expense Detail Modal</h3>
      <div className="screen-simulation detail-modal">
        <div className="sim-header">Expense Details</div>
        <div className="sim-detail-content">
          {/* Header */}
          <div className="sim-detail-header">
            <div className="sim-detail-title">
              <span className="sim-category-icon">🍽️</span>
              <div>
                <h4>Group dinner at restaurant</h4>
                <span className="sim-meta">25 Dec 2024 • Food & Drinks</span>
              </div>
            </div>
            <div className="sim-detail-amount">€150.00</div>
          </div>

          {/* Paid By */}
          <div className="sim-detail-section">
            <h5>💳 Paid By</h5>
            <div className="sim-detail-list">
              <div className="sim-detail-row green">
                <span>
                  <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
                  Elias
                </span>
                <span>+€80.00</span>
              </div>
              <div className="sim-detail-row green">
                <span>
                  <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
                  Maria
                </span>
                <span>+€50.00</span>
              </div>
              <div className="sim-detail-row green">
                <span>
                  <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
                  Dimitroula
                </span>
                <span>+€20.00</span>
              </div>
            </div>
          </div>

          {/* Split Between */}
          <div className="sim-detail-section">
            <h5>📊 Split Between</h5>
            <div className="sim-detail-list">
              <div className="sim-detail-row">
                <span>
                  <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
                  Elias
                </span>
                <span>€50.00</span>
              </div>
              <div className="sim-detail-row">
                <span>
                  <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
                  Maria
                </span>
                <span>€50.00</span>
              </div>
              <div className="sim-detail-row">
                <span>
                  <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
                  Dimitroula
                </span>
                <span>€50.00</span>
              </div>
            </div>
          </div>

          {/* Net Effect */}
          <div className="sim-detail-section">
            <h5>📈 Net Effect</h5>
            <div className="sim-detail-list">
              <div className="sim-detail-row">
                <span>
                  <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
                  Elias
                </span>
                <span className="positive">+€30.00</span>
              </div>
              <div className="sim-detail-row">
                <span>
                  <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
                  Maria
                </span>
                <span className="neutral">€0.00</span>
              </div>
              <div className="sim-detail-row">
                <span>
                  <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
                  Dimitroula
                </span>
                <span className="negative">-€30.00</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h3>📊 Understanding Net Effect</h3>
      <div className="calculation-box">
        <div className="calc-formula">
          <strong>Net Effect</strong> = Amount Paid − Amount Owed
        </div>
        <div className="calc-examples">
          <div className="calc-row">
            <span>Elias:</span>
            <span>€80 paid − €50 owed = <strong className="positive">+€30.00</strong></span>
          </div>
          <div className="calc-row">
            <span>Maria:</span>
            <span>€50 paid − €50 owed = <strong>€0.00</strong></span>
          </div>
          <div className="calc-row">
            <span>Dimitroula:</span>
            <span>€20 paid − €50 owed = <strong className="negative">−€30.00</strong></span>
          </div>
        </div>
      </div>

      <div className="legend-box">
        <h4>🎨 Color Legend</h4>
        <div className="legend-items">
          <div className="legend-item">
            <span className="legend-color positive"></span>
            <span><strong>Green (+):</strong> Others owe this person money</span>
          </div>
          <div className="legend-item">
            <span className="legend-color negative"></span>
            <span><strong>Red (−):</strong> This person owes others money</span>
          </div>
          <div className="legend-item">
            <span className="legend-color neutral"></span>
            <span><strong>Neutral (€0):</strong> Paid exactly their share</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Default Settings
// ===============================================================================
function DefaultsSection({ members }) {
  return (
    <div className="help-section">
      <h2>⚙️ Default Settings</h2>
      
      <p>
        Group admins can set <strong>default percentages</strong> and <strong>default shares</strong> 
        for each member. These are used as starting values when creating new expenses.
      </p>

      <h3>📊 Default Percentages</h3>
      <p>Found in the <strong>Members tab</strong>. Set how expenses are typically split.</p>
      
      <div className="screen-simulation">
        <div className="sim-section-header">📊 Default Split Percentages</div>
        <div className="sim-split-list">
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
            </span>
            <div className="sim-display-value">50.0%</div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
              Maria
            </span>
            <div className="sim-display-value">30.0%</div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
              Dimitroula
            </span>
            <div className="sim-display-value">20.0%</div>
          </div>
          <div className="sim-total valid">
            Total: 100.0% ✓
          </div>
        </div>
        <div className="sim-buttons">
          <button className="sim-btn secondary">⚖️ Equal</button>
          <button className="sim-btn primary">💾 Save</button>
        </div>
      </div>

      <div className="warning-box">
        <div className="warning-icon">⚠️</div>
        <div className="warning-content">
          <strong>Important:</strong> Percentages must always sum to exactly <strong>100%</strong>
        </div>
      </div>

      <h3>👥 Default Shares</h3>
      <p>Set the typical number of shares (member + regular guests) for each person.</p>
      
      <div className="screen-simulation">
        <div className="sim-section-header">👥 Default Shares (Guests)</div>
        <p className="sim-hint">Set default guest count per member</p>
        <div className="sim-split-list">
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span>
              Elias
            </span>
            <div className="sim-display-value">1 share</div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span>
              Maria
            </span>
            <div className="sim-display-value">4 shares</div>
          </div>
          <div className="sim-split-row">
            <span className="sim-member-name">
              <span className="mini-avatar" style={{ background: "#7ab592" }}>D</span>
              Dimitroula
            </span>
            <div className="sim-display-value">2 shares</div>
          </div>
        </div>
        <div className="sim-buttons">
          <button className="sim-btn secondary">🔄 Reset to 1</button>
          <button className="sim-btn primary">💾 Save</button>
        </div>
      </div>

      <div className="tip-box">
        <div className="tip-icon">💡</div>
        <div className="tip-content">
          <strong>Tip:</strong> Default shares are useful when Maria always brings her family 
          to group events. Set her default to 4 shares, and it will auto-fill every time!
        </div>
      </div>
    </div>
  );
}

// ===============================================================================
// SECTION: Full Examples
// ===============================================================================
function FullExamplesSection({ members, currency }) {
  return (
    <div className="help-section">
      <h2>📝 Full Examples</h2>
      
      <p>Here are complete, step-by-step examples showing different scenarios.</p>

      {/* Example 1 */}
      <div className="full-example">
        <div className="example-header">
          <span className="example-number">Example 1</span>
          <h3>🍕 Pizza Night - Single Payer, Equal Split</h3>
        </div>
        <div className="example-content">
          <div className="example-scenario">
            <strong>Scenario:</strong> Elias orders pizza for everyone. Total: €60
          </div>
          
          <div className="example-steps">
            <div className="example-step">
              <div className="step-num">1</div>
              <div className="step-detail">
                <strong>Amount:</strong> €60.00
                <br/>
                <strong>Description:</strong> "Pizza delivery"
              </div>
            </div>
            <div className="example-step">
              <div className="step-num">2</div>
              <div className="step-detail">
                <strong>Multiple Payers:</strong> ☐ Unchecked
                <br/>
                <span className="step-note">Elias is logged in, so he's the payer</span>
              </div>
            </div>
            <div className="example-step">
              <div className="step-num">3</div>
              <div className="step-detail">
                <strong>Split:</strong> Click "= Equal"
                <br/>
                Each person: €60 ÷ 3 = €20.00
              </div>
            </div>
          </div>

          <div className="example-result">
            <h4>📊 Result</h4>
            <table className="result-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Paid</th>
                  <th>Owes</th>
                  <th>Net</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span> Elias</td>
                  <td className="positive">€60.00</td>
                  <td>€20.00</td>
                  <td className="positive">+€40.00</td>
                </tr>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span> Maria</td>
                  <td>€0.00</td>
                  <td>€20.00</td>
                  <td className="negative">−€20.00</td>
                </tr>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#7ab592" }}>D</span> Dimitroula</td>
                  <td>€0.00</td>
                  <td>€20.00</td>
                  <td className="negative">−€20.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Example 2 */}
      <div className="full-example">
        <div className="example-header">
          <span className="example-number">Example 2</span>
          <h3>🏠 Rent - Single Payer, Percentage Split</h3>
        </div>
        <div className="example-content">
          <div className="example-scenario">
            <strong>Scenario:</strong> Maria pays the monthly rent of €1,200. 
            Split according to room sizes: Elias 50%, Maria 30%, Dimitroula 20%
          </div>
          
          <div className="example-steps">
            <div className="example-step">
              <div className="step-num">1</div>
              <div className="step-detail">
                <strong>Amount:</strong> €1,200.00
                <br/>
                <strong>Description:</strong> "December rent"
              </div>
            </div>
            <div className="example-step">
              <div className="step-num">2</div>
              <div className="step-detail">
                <strong>Multiple Payers:</strong> ☐ Unchecked
                <br/>
                <span className="step-note">Maria pays alone</span>
              </div>
            </div>
            <div className="example-step">
              <div className="step-num">3</div>
              <div className="step-detail">
                <strong>Split:</strong> Use "% Default" tab
                <br/>
                Elias: €600, Maria: €360, Dimitroula: €240
              </div>
            </div>
          </div>

          <div className="example-result">
            <h4>📊 Result</h4>
            <table className="result-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Paid</th>
                  <th>Owes</th>
                  <th>Net</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span> Elias</td>
                  <td>€0.00</td>
                  <td>€600.00</td>
                  <td className="negative">−€600.00</td>
                </tr>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span> Maria</td>
                  <td className="positive">€1,200.00</td>
                  <td>€360.00</td>
                  <td className="positive">+€840.00</td>
                </tr>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#7ab592" }}>D</span> Dimitroula</td>
                  <td>€0.00</td>
                  <td>€240.00</td>
                  <td className="negative">−€240.00</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Example 3 */}
      <div className="full-example">
        <div className="example-header">
          <span className="example-number">Example 3</span>
          <h3>🎉 Birthday Party - Multiple Payers, Shares Split</h3>
        </div>
        <div className="example-content">
          <div className="example-scenario">
            <strong>Scenario:</strong> Birthday party costs €200. Multiple people pay, 
            and Maria brought her husband and 2 kids (4 shares total).
            <br/><br/>
            <strong>Who paid:</strong> Elias €100, Dimitroula €100
            <br/>
            <strong>Shares:</strong> Elias: 1, Maria: 4 (+ family), Dimitroula: 2 (+ partner)
            <br/>
            <strong>Total shares:</strong> 7
          </div>
          
          <div className="example-steps">
            <div className="example-step">
              <div className="step-num">1</div>
              <div className="step-detail">
                <strong>Amount:</strong> €200.00
                <br/>
                <strong>Description:</strong> "Dimitroula's birthday party"
              </div>
            </div>
            <div className="example-step">
              <div className="step-num">2</div>
              <div className="step-detail">
                <strong>Multiple Payers:</strong> ☑️ Checked
                <br/>
                Elias: €100, Maria: €0, Dimitroula: €100
              </div>
            </div>
            <div className="example-step">
              <div className="step-num">3</div>
              <div className="step-detail">
                <strong>Split:</strong> Use "👥 Shares" tab
                <br/>
                Elias: 1, Maria: 4, Dimitroula: 2
                <br/>
                Per share: €200 ÷ 7 = €28.57
              </div>
            </div>
          </div>

          <div className="example-result">
            <h4>📊 Result</h4>
            <table className="result-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Shares</th>
                  <th>Paid</th>
                  <th>Owes</th>
                  <th>Net</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#2d5a3d" }}>E</span> Elias</td>
                  <td>1</td>
                  <td className="positive">€100.00</td>
                  <td>€28.57</td>
                  <td className="positive">+€71.43</td>
                </tr>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#5a8f6e" }}>M</span> Maria</td>
                  <td>4</td>
                  <td>€0.00</td>
                  <td>€114.29</td>
                  <td className="negative">−€114.29</td>
                </tr>
                <tr>
                  <td><span className="mini-avatar" style={{ background: "#7ab592" }}>D</span> Dimitroula</td>
                  <td>2</td>
                  <td className="positive">€100.00</td>
                  <td>€57.14</td>
                  <td className="positive">+€42.86</td>
                </tr>
              </tbody>
            </table>
            <div className="result-note">
              <strong>Settlement needed:</strong> Maria owes €71.43 to Elias and €42.86 to Dimitroula
            </div>
          </div>
        </div>
      </div>

      {/* Quick Reference */}
      <div className="quick-reference">
        <h3>📌 Quick Reference</h3>
        <table className="reference-table">
          <thead>
            <tr>
              <th>Scenario</th>
              <th>Multiple Payers</th>
              <th>Split Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>One person pays, split equally</td>
              <td>☐ Off</td>
              <td>= Equal</td>
            </tr>
            <tr>
              <td>One person pays, custom percentages</td>
              <td>☐ Off</td>
              <td>% Default</td>
            </tr>
            <tr>
              <td>Multiple people pay together</td>
              <td>☑️ On</td>
              <td>Any</td>
            </tr>
            <tr>
              <td>Members brought guests</td>
              <td>Any</td>
              <td>👥 Shares</td>
            </tr>
            <tr>
              <td>Different room sizes (rent)</td>
              <td>☐ Off</td>
              <td>% Default</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ExpenseHelp;

